// Tips translations
// Contains localized content for all tips including Markdown bodies

export const tipsTranslations = {
  en: {
    tips: {
      doNotShowAgain: 'Do not show again',

      mainPage: {
        title: 'Welcome to the 3D Viewer',
        body: `
Here are some tips to help you get started:

### Navigation Controls
- **Left-click + drag**: Pan the view
- **Right-click + drag**: Zoom smoothly
- **Scroll wheel**: Zoom in/out
- **Middle-click + drag**: Rotate the view

### Right side quick buttons
- **Click the compass ring**: Rotate the view around the scene  
- **Use the joystick**: Tilt and orient the camera  
- **+**: Zoom in  
- **Home**: Return to the dataset (use this if you get lost)  
- **−**: Zoom out  

### Upper left side toolbar
- **Measurements**: Measure distances, heights, and areas
- **Viewpoints**: Save and restore camera positions
- **Layers**: Change base maps, imagery, and terrain
- **Clipping**: Create clipping planes to see inside the data
- **Profiler**: Generate cross-section profiles of the point cloud
- **Settings**: Adjust point cloud rendering and display options

### Tips
Each tool has its own tips that will appear when you first open it. Look for the lightbulb icon!
`,
      },

      settings: {
        title: 'Settings Tool',
        body: `
Adjust how the point cloud and the terrain is rendered:

### Point Cloud
- **Point Size**: Change the size of each point
- **Property**: Change the property displayed (Color, Intensity, Classification, ...)
- **Eye-Dome Lighting (EDL)**: Enhance depth perception

### World Settings
- **Globe Lighting**: Toggle realistic lighting
- **Atmosphere**: Show/hide atmospheric effects

### Terrain
- **Terrain**: Enable 3D terrain
- **Contour Lines**: Display elevation contours

### Camera
- **Field of View**: Adjust the camera's field of view
- **Zoom Speed**: Control how fast you zoom

### Rendering
- **Reduce pixel density**: render at the browser's recommended resolution. Check this to reduce the resolution.
`,
      },

      layers: {
        title: 'Layers Tool',
        body: `
Control the basemap rendering:

### Imagery Layers
Select satellite or aerial imagery:
- Bing Maps
- OpenStreetMap
- And more...

### Terrain Layers
Enable 3D terrain to see elevation changes. In Japan, we recommend using Japan Regional Terrain (GSI)

### Building Layers
Add 3D buildings:
- **Cesium OSM Buildings**: OpenStreetMap buildings worldwide
- **Japan 3D (Plateau)**: Detailed Japanese city models

### Global Layer
Provided by Google, it contains Imagery, Terrain and building layers combined.
`,
      },

      models: {
        title: 'Models Tool',
        body: `
Manage 3D models attached to the dataset:

### Supported Formats
- **OBJ**: Standard 3D model format
- **GeoJSON**: Forest report in the form of chest-height cylinders

### Model Controls
- Toggle visibility of individual models
- Adjust model color
`,
      },

      measurements: {
        title: 'Measurement Tools',
        body: `
Measure distances, heights, areas, and angles in 3D:

### Available Measurements
- **Distance**: Click 2 points to measure distance
- **Height**: Click a point to measure height above ground
- **Angle**: Click 3 points to measure an angle
- **Area**: Left-click to add points, right-click to close polygon
- **Circle**: Click 3 points to define a circle

### Tips
- Measurements are saved automatically
- Export measurements to JSON for later use
- Import previously saved measurements

### Controls
- Click the measurement type button to start
- Follow the on-screen instructions
- Right-click to finish or cancel
`,
      },

      clipping: {
        title: 'Clipping Planes',
        body: `
Cut through the point cloud to see inside:

### How to Use
1. Enable clipping using the toggle
2. Drag the clipping planes to move them
3. Adjust the clipping box to your needs

### Controls
- **Left-drag**: Move the clipping plane
- **Reset**: Return planes to original position

### Tips
- Use clipping to reveal internal structures
- Combine multiple planes for complex cuts
`,
      },

      profiler: {
        title: 'Profile Tool',
        body: `
Generate cross-section profiles of the point cloud:

### How to Create a Profile
1. Click on the **Create** button to begin
2. Click 2 points to define the profile line
3. Adjust the width as needed
4. View the profile in the side panel

### Profile View
The profile view shows:
- Elevation distribution
- Point density along the section
- Interactive cursor to identify points

### Tips
- Create multiple profiles for comparison
- Adjust width to capture more or fewer points
`,
      },

      viewpoints: {
        title: 'Viewpoints',
        body: `
Save and restore camera positions:

### Saving a Viewpoint
1. Navigate to the desired view
2. Click **Save Current Viewpoint**

### Restoring a Viewpoint
Click on a saved viewpoint to instantly return to that view.

### Managing Viewpoints
- **Export**: Save viewpoints to a file
- **Import**: Load previously saved viewpoints
- **Delete**: Remove individual viewpoints
- **Clear All**: Remove all viewpoints

### Tips
- Use viewpoints for presentations
- Share viewpoints with colleagues via export/import
`,
      },

      gaussianSplatting: {
        title: 'Gaussian Splatting',
        body: `
View photorealistic 3D reconstructions:

### What is Gaussian Splatting?
Gaussian Splatting is a technique for rendering photorealistic 3D scenes from a set of images.

### View Modes
- **Full Screen**: View splat in full screen
- **Split Screen**: Compare with point cloud side by side

### Tips
- Gaussian splatting provides more realistic visualization
- Combine with point cloud for analysis
`,
      },

      transformation: {
        title: 'Transformation Tool',
        body: `
Adjust the position, rotation, and scale of your datasets directly in the 3D view.

### Controls
Each row has a **slider** for quick adjustments and a **text field** for precise values. Use the **−** / **+** buttons for fine-tuning by one step at a time.

### Translation (tx / ty / tz)
Move the dataset along the local East (tx), North (ty), and Up (tz) axes. Useful for correcting height offsets, such as the geoid undulation.

### Rotation (rx / ry / rz)
Rotate the dataset around the local X (roll), Y (pitch), and Z (heading) axes.

### Scale (sx / sy / sz)
Scale the dataset independently along each axis.

### Dataset selector
When both a point cloud and Gaussian Splatting are loaded, use the selector to choose which dataset to adjust, or apply the same transform to **Both** simultaneously.

### Tips
- Transforms are applied in the local ENU frame (East–North–Up) at the dataset center
- Values are preserved when you close and reopen the panel
`,
      },

      annotations: {
        title: 'Annotation Tools',
        body: `
Add notes and markers to specific locations in 3D space:

### Creating Annotations
1. Click the **pin button** to enable placement mode
2. Click anywhere on the point cloud or terrain
3. Enter your annotation text in the dialog
4. The annotation appears as a pin with a label

### Managing Annotations
- **Click an annotation** in the list to highlight it
- **Fly to**: Navigate directly to an annotation's location
- **Delete**: Remove individual annotations
- **Clear All**: Remove all annotations at once

### Import & Export
- **Export**: Save all annotations to a JSON file
- **Import**: Load previously saved annotations

### Tips
- Annotations are useful for marking points of interest
- Share annotation files with colleagues for collaboration
- Camera position is saved with each annotation
`,
      },
    },
  },

  jp: {
    tips: {
      doNotShowAgain: '今後表示しない',

      mainPage: {
        title: '3Dビューアへようこそ',
        body: `
使い始めるためのヒントをご紹介します：

### ナビゲーション操作
- **左クリック + ドラッグ**: ビューをパン
- **右クリック + ドラッグ**: スムーズにズーム
- **スクロールホイール**: ズームイン/アウト
- **中クリック + ドラッグ**: ビューを回転

### 右側のクイックボタン
- **コンパスリングをクリック**: シーンの周りでビューを回転
- **ジョイスティックを使用**: カメラの傾きと方向を調整
- **+**: ズームイン
- **Home**: データセットに戻る（迷った場合はこれを使用）
- **−**: ズームアウト

### 左上のツールバー
- **測定**: 距離、高さ、面積を測定
- **ビューポイント**: カメラ位置を保存・復元
- **レイヤー**: ベースマップ、画像、地形を変更
- **クリッピング**: データの内部を見るためのクリッピング面を作成
- **プロファイラー**: 点群の断面プロファイルを生成
- **設定**: 点群のレンダリングと表示オプションを調整

### ヒント
各ツールには初めて開いたときに表示されるヒントがあります。電球アイコンをご覧ください！
`,
      },

      settings: {
        title: '設定ツール',
        body: `
点群と地形のレンダリング方法を調整します：

### 点群
- **点のサイズ**: 各点のサイズを変更
- **プロパティ**: 表示するプロパティを変更（色、強度、分類など）
- **アイドームライティング (EDL)**: 奥行き感を強調

### ワールド設定
- **グローブライティング**: リアルな照明を切り替え
- **大気**: 大気効果の表示/非表示

### 地形
- **地形**: 3D地形を有効化
- **等高線**: 標高の等高線を表示

### カメラ
- **視野角**: カメラの視野角を調整
- **ズーム速度**: ズームの速さを制御

### レンダリング
- **画素密度を低減する**: ブラウザの推奨解像度でレンダリング。解像度を下げるにはこちらをチェック。
`,
      },

      layers: {
        title: 'レイヤーツール',
        body: `
ベースマップのレンダリングを制御します：

### 画像レイヤー
衛星画像または航空写真を選択：
- Bing Maps
- OpenStreetMap
- その他...

### 地形レイヤー
標高変化を見るために3D地形を有効化します。日本では、日本地域地形（GSI）の使用をお勧めします。

### 建物レイヤー
3D建物を追加：
- **Cesium OSM Buildings**: 世界中のOpenStreetMap建物
- **Japan 3D (Plateau)**: 詳細な日本の都市モデル

### グローバルレイヤー
Googleが提供する、画像・地形・建物レイヤーを組み合わせたレイヤーです。
`,
      },

      models: {
        title: 'モデルツール',
        body: `
データセットに関連付けられた3Dモデルを管理します：

### サポートされている形式
- **OBJ**: 標準的な3Dモデル形式
- **GeoJSON**: 胸高円柱形式の森林レポート

### モデルコントロール
- 個々のモデルの表示を切り替え
- モデルの色を調整
`,
      },

      measurements: {
        title: '測定ツール',
        body: `
3Dで距離、高さ、面積、角度を測定します：

### 利用可能な測定
- **距離**: 2点をクリックして距離を測定
- **高さ**: 点をクリックして地面からの高さを測定
- **角度**: 3点をクリックして角度を測定
- **面積**: 左クリックで点を追加、右クリックでポリゴンを閉じる
- **円**: 3点をクリックして円を定義

### ヒント
- 測定は自動的に保存されます
- 後で使用するためにJSONにエクスポート
- 以前に保存した測定をインポート

### コントロール
- 測定タイプボタンをクリックして開始
- 画面上の指示に従う
- 右クリックで終了またはキャンセル
`,
      },

      clipping: {
        title: 'クリッピング面',
        body: `
点群を切断して内部を確認します：

### 使い方
1. トグルを使用してクリッピングを有効化
2. クリッピング面をドラッグして移動
3. 必要に応じてクリッピングボックスを調整

### コントロール
- **左ドラッグ**: クリッピング面を移動
- **リセット**: 面を元の位置に戻す

### ヒント
- クリッピングを使用して内部構造を表示
- 複雑なカットには複数の面を組み合わせる
`,
      },

      profiler: {
        title: 'プロファイルツール',
        body: `
点群の断面プロファイルを生成します：

### プロファイルの作成方法
1. **新しいプロフィールを作成**ボタンをクリックして開始
2. 2点をクリックしてプロファイル線を定義
3. 必要に応じて幅を調整
4. サイドパネルでプロファイルを表示

### プロファイルビュー
プロファイルビューには以下が表示されます：
- 標高分布
- セクションに沿った点密度
- 点を識別するためのインタラクティブカーソル

### ヒント
- 比較のために複数のプロファイルを作成
- 幅を調整してより多くまたは少ない点をキャプチャ
`,
      },

      viewpoints: {
        title: 'ビューポイント',
        body: `
カメラ位置を保存して復元します：

### ビューポイントの保存
1. 目的のビューに移動
2. **現在のビューポイントを保存**をクリック

### ビューポイントの復元
保存されたビューポイントをクリックすると、そのビューに即座に戻ります。

### ビューポイントの管理
- **エクスポート**: ビューポイントをファイルに保存
- **インポート**: 以前に保存したビューポイントを読み込み
- **削除**: 個々のビューポイントを削除
- **すべてクリア**: すべてのビューポイントを削除

### ヒント
- プレゼンテーションにビューポイントを使用
- エクスポート/インポートで同僚とビューポイントを共有
`,
      },

      gaussianSplatting: {
        title: 'ガウシアンスプラッティング',
        body: `
フォトリアリスティックな3D再構成を表示します：

### ガウシアンスプラッティングとは？
ガウシアンスプラッティングは、画像のセットからフォトリアリスティックな3Dシーンをレンダリングする技術です。

### 表示モード
- **フルスクリーン**: フルスクリーンでスプラットを表示
- **分割スクリーン**: 点群と並べて比較

### ヒント
- ガウシアンスプラッティングはより現実的な可視化を提供
- 分析のために点群と組み合わせる
`,
      },

      transformation: {
        title: '変換ツール',
        body: `
データセットの位置・回転・スケールを3Dビュー上で直接調整します。

### 操作方法
各行に**スライダー**（素早い調整用）と**テキスト欄**（正確な値の入力用）があります。**−** / **+** ボタンで1ステップずつ微調整できます。

### 平行移動（tx / ty / tz）
データセットをローカルの東（tx）・北（ty）・上（tz）方向に移動します。ジオイド高の補正など、高さのオフセット修正に便利です。

### 回転（rx / ry / rz）
ローカルのX軸（ロール）・Y軸（ピッチ）・Z軸（ヘディング）を中心にデータセットを回転させます。

### スケール（sx / sy / sz）
各軸に沿ってデータセットを個別にスケーリングします。

### データセット選択
点群とガウシアンスプラッティングの両方が読み込まれている場合、セレクターで調整対象を選択するか、**両方**に同じ変換を適用できます。

### ヒント
- 変換はデータセット中心のローカルENUフレーム（東・北・上）で適用されます
- パネルを閉じて再度開いても値は保持されます
`,
      },

      annotations: {
        title: 'アノテーションツール',
        body: `
3D空間の特定の場所にメモやマーカーを追加します：

### アノテーションの作成
1. **ピンボタン**をクリックして配置モードを有効化
2. 点群または地形の任意の場所をクリック
3. ダイアログにアノテーションテキストを入力
4. ピンとラベル付きのアノテーションが表示されます

### アノテーションの管理
- **リスト内のアノテーションをクリック**: ハイライト表示
- **移動**: アノテーションの場所に直接移動
- **削除**: 個々のアノテーションを削除
- **すべてクリア**: すべてのアノテーションを一度に削除

### インポート＆エクスポート
- **エクスポート**: すべてのアノテーションをJSONファイルに保存
- **インポート**: 以前に保存したアノテーションを読み込み

### ヒント
- アノテーションは注目点のマーキングに便利です
- 同僚とアノテーションファイルを共有してコラボレーション
- 各アノテーションにはカメラ位置が保存されます
`,
      },
    },
  },
};
